/**
 * The lifecycle buttons for one return (FR-5, FR-9, FR-E6, FR-E17) — Prepare, Sign off (with
 * what was checked), Send for approval (which sends the client their approval link, FR-E15),
 * Record the client's approval yourself (the fallback), Record filing with the
 * acknowledgment number, Confirm with the filed return, Lock.
 *
 * Extracted from `Periods.tsx` for e2e-filing C-02 so the GSTR-3B screen offers the same strip
 * for the 3B period, rather than a second copy that would drift from this one. Which actions a
 * state offers, and which artifact's hash the approval presents, are `lib/lifecycle.ts`'s rules;
 * this renders them and sends what a person typed. The service decides — a refusal's sentence
 * is shown by the caller's `act`.
 */

import { Button } from '@fiveox/ui'
import { useState } from 'react'

import { type ErrorReportUpload, type PeriodRecord, filing, periods } from '../lib/api'
import { canReceiveErrorReport, isUnconfirmed } from '../lib/filing'
import { boundSha, offeredActions, offersRecordFiling } from '../lib/lifecycle'
import { laneOff } from '../lib/portalFetch'
import { useFilingLane } from '../lib/useFilingLane'

export function LifecycleActions({
  period,
  artifacts,
  busy,
  act,
  onErrorReport,
}: {
  period: PeriodRecord
  artifacts: Array<{ kind: string; status: string; sha256: string }>
  busy: boolean
  /** Runs one call, reloads the screen, shows a refusal's sentence. */
  act: (fn: () => Promise<unknown>) => Promise<unknown>
  /**
   * FR-E6: the portal's error report, offered on a client-approved GSTR-1 when the caller can
   * show what landing it answered. Absent, the control is not offered.
   */
  onErrorReport?: (body: ErrorReportUpload) => void
}) {
  /**
   * Sign-off is the one transition that is a judgement rather than a mechanism, so it asks what
   * was checked. Not pre-filled: a confirmation you satisfy by pressing nothing is weaker than
   * one you satisfy by pressing the space bar, and the server refuses that one.
   */
  const [signOffRemark, setSignOffRemark] = useState<string | null>(null)
  const [ackNo, setAckNo] = useState('')
  const [filedFile, setFiledFile] = useState<File | null>(null)
  const [errorReportFile, setErrorReportFile] = useState<File | null>(null)
  /** D-12: what fetching the filed return from the portal came to, in the service's words. */
  const [fetched, setFetched] = useState<string | null>(null)
  const lane = useFilingLane(period.client_id)
  const off = laneOff(lane)
  /** FR-E15: what sending did about the client's approval link — sent until when, or why not. */
  const [linkWords, setLinkWords] = useState<string | null>(null)

  const currentSha = boundSha(period, artifacts)
  const offered = offeredActions(period, currentSha)

  const run = (action: string) => {
    switch (action) {
      case 'send_for_approval':
        return periods.sendForApproval(period.id).then((sent) => {
          setLinkWords(sent.data.client_link?.words ?? null)
          return sent
        })
      case 'approve':
        return periods.transition(period.id, { action, artifact_sha256: currentSha })
      case 'sign_off':
        return periods.transition(period.id, { action, remark: signOffRemark ?? '' })
      default:
        return periods.transition(period.id, { action })
    }
  }

  return (
    <span className="flex flex-wrap gap-2">
      {offered.map((a, i) => (
        <Button
          key={a.action}
          type="button"
          disabled={busy}
          onClick={() => (a.prompt ? setSignOffRemark('') : void act(() => run(a.action)))}
          variant={i === 0 ? 'primary' : 'outline'}
          size="sm"
        >
          {a.label}
        </Button>
      ))}
      {linkWords && period.state === 'sent_for_approval' && (
        <span className="w-full text-sm text-muted-foreground">{linkWords}</span>
      )}
      {signOffRemark !== null && (
        <span className="flex w-full flex-wrap items-center gap-2">
          <input
            value={signOffRemark}
            onChange={(e) => setSignOffRemark(e.target.value.slice(0, 500))}
            placeholder="What did you check before signing off?"
            className="gst-input min-w-72 flex-1"
          />
          <Button
            type="button"
            disabled={busy || !signOffRemark.trim()}
            onClick={() =>
              void act(async () => {
                await run('sign_off')
                setSignOffRemark(null)
              })
            }
            size="sm"
          >
            Sign off
          </Button>
          <Button type="button" onClick={() => setSignOffRemark(null)} variant="outline" size="sm">
            Cancel
          </Button>
        </span>
      )}
      {/*
        * Finding A1 (stage 5b). The list told people to upload the filed return to confirm a
        * filing, and there was nothing to upload it with. The portal's own file is the receipt;
        * the ARN beside it is somebody's word. A filed GSTR-3B is stored and confirms the
        * filing; its figures are not read yet (C-02 — the reader is Wave E's).
        */}
      {isUnconfirmed(period) && (
        <>
          <input
            type="file"
            accept="application/json,.json"
            aria-label="The portal's filed return (.json)"
            onChange={(e) => setFiledFile(e.target.files?.[0] ?? null)}
            className="gst-input"
          />
          <Button
            type="button"
            disabled={!filedFile || busy}
            onClick={() =>
              void act(async () => {
                if (!filedFile) return
                await periods.confirmFiling(period.id, filedFile)
                setFiledFile(null)
              })
            }
            size="sm"
          >
            Confirm with the filed return
          </Button>
          {/*
            * D-12: the same return fetched from the portal, kept exactly as the upload is — the
            * upload stays as the fallback. Built against recorded fixtures, not yet run against
            * GSTN; with the lane off the button is disabled and says why.
            */}
          {(period.return_type === 'GSTR1' || period.return_type === 'GSTR3B') && (
            <Button
              type="button"
              disabled={busy || !lane || !!off}
              title={off ?? undefined}
              onClick={() =>
                void act(async () => {
                  const body = await filing.fetchFiled(period.id)
                  setFetched(body.data.sentence)
                })
              }
              variant="outline"
              size="sm"
            >
              Fetch the filed return from the portal
            </Button>
          )}
          {off && <span className="gst-meta w-full">Fetching from the portal: {off}</span>}
          {fetched && <span className="gst-meta w-full">{fetched}</span>}
        </>
      )}
      {/*
        * FR-E6. The portal rejected the upload: its error report lands on the rows it names and
        * the return goes back to Prepared. Offered where the JSON could have been uploaded from —
        * a client-approved GSTR-1 — and nowhere a filed return stands.
        */}
      {onErrorReport && canReceiveErrorReport(period) && (
        <>
          <input
            type="file"
            accept="application/json,.json"
            aria-label="The portal's error report (.json)"
            onChange={(e) => setErrorReportFile(e.target.files?.[0] ?? null)}
            className="gst-input"
          />
          <Button
            type="button"
            disabled={!errorReportFile || busy}
            onClick={() =>
              void act(async () => {
                if (!errorReportFile) return
                const body = await periods.uploadErrorReport(period.id, errorReportFile)
                onErrorReport(body.data)
                setErrorReportFile(null)
              })
            }
            variant="outline"
            size="sm"
          >
            Upload the portal's error report
          </Button>
        </>
      )}
      {offersRecordFiling(period) && (
        <>
          <input
            value={ackNo}
            onChange={(e) => setAckNo(e.target.value)}
            placeholder="GSTN acknowledgment no"
            className="gst-input w-44"
          />
          <Button
            type="button"
            disabled={!ackNo.trim() || busy}
            onClick={() =>
              void act(async () => {
                await periods.transition(period.id, { action: 'record_filing', ack_no: ackNo.trim() })
                setAckNo('')
              })
            }
            variant="outline"
            size="sm"
          >
            Record filing
          </Button>
        </>
      )}
    </span>
  )
}
